/**
 * Data formatting and export utilities
 */
import { format } from 'date-fns';

/**
 * Format date to YYYY-MM-DD in local timezone (not UTC)
 */
const formatDateLocal = (date) => {
  return format(date, 'yyyy-MM-dd');
};

/**
 * Generate CSV template for algae samples
 * @param {Array} organisms - List of organisms
 * @param {Array} customOrganisms - Custom organisms
 * @param {Array} locations - List of locations
 * @param {Date} sampleDate - Sample date for filename
 * @returns {void} Downloads CSV file
 */
export const downloadAlgaeTemplate = (organisms, customOrganisms, locations, sampleDate) => {
  // Generate CSV template matching current Excel format
  const headers = [
    'Orden/gruppe', 
    'Evt. art', 
    'Provdjup (m)', 
    ...locations.map(loc => {
      const locationName = loc ? (loc.split(' - ')[1] || loc) : 'Ny';
      return `${locationName} (ant/l)`;
    })
  ];
  const csvContent = [headers.join(',')];
  
  [...organisms, ...customOrganisms].forEach(org => {
    if (!org.isSum && !org.isTotal) {
      const row = [
        org.group, 
        org.species, 
        org.depth, 
        ...new Array(locations.length).fill('')
      ];
      csvContent.push(row.join(','));
    }
  });
  
  const blob = new Blob([csvContent.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `algeprover_template_${formatDateLocal(sampleDate)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Parse clipboard data (from Excel) into 2D array
 * @param {string} clipboardText - Raw clipboard text
 * @returns {Array<Array<string>>} 2D array of parsed data
 */
export const parseClipboardData = (text) => {
  // Split by newlines to get rows
  const rows = text.trim().split('\n').filter(row => row.trim());
  
  // Parse each row by tabs (Excel format)
  return rows.map(row => {
    return row.split('\t').map(cell => {
      const trimmed = cell.trim();
      
      // Handle <5000 as special case
      if (trimmed.match(/^<\s*\d+$/)) {
        return '<5000';
      }
      
      // Remove commas and parse number
      const cleaned = trimmed.replace(/,/g, '');
      
      // Check if it's a valid number
      if (cleaned && !isNaN(cleaned)) {
        return cleaned;
      }
      
      return trimmed;
    });
  });
};

/**
 * Format number for display (with thousand separators)
 * @param {string|number} value - Value to format
 * @returns {string} Formatted number string
 */
export const formatNumber = (value) => {
  if (!value || value === '') return '-';
  if (value === '<5000') return '<5000';
  
  const numValue = typeof value === 'string' ? parseInt(value) : value;
  if (isNaN(numValue)) return value;
  
  return numValue.toLocaleString('nb-NO');
};

/**
 * Prepare algae data for API submission
 * @param {Object} gridData - Grid data object
 * @param {Array} organisms - List of organisms
 * @param {Array} customOrganisms - Custom organisms
 * @param {Array} locations - List of locations
 * @returns {Array} Array of sample objects
 */
export const prepareAlgaeDataForAPI = (gridData, organisms, customOrganisms, locations) => {
  const samples = [];
  
  [...organisms, ...customOrganisms].forEach(organism => {
    if (organism.isSum || organism.isTotal) return;
    
    const organismKey = `${organism.group}_${organism.species}`;
    
    locations.forEach(location => {
      const value = gridData[organismKey]?.[location];
      if (value && value.trim()) {
        samples.push({
          organism_group: organism.group,
          organism_species: organism.species,
          location,
          value: value,
          depth: organism.depth || 0
        });
      }
    });
  });
  
  return samples;
};

/**
 * Prepare zooplankton data for API submission
 * @param {Object} zooplanktonGridData - Zooplankton grid data
 * @param {Array} currentZooplanktonOrganisms - Current zooplankton organisms
 * @param {Array} locations - List of locations
 * @returns {Array} Array of zooplankton sample objects
 */
export const prepareZooplanktonDataForAPI = (zooplanktonGridData, currentZooplanktonOrganisms, locations) => {
  const samples = [];
  
  currentZooplanktonOrganisms.forEach(org => {
    const organismKey = `${org.sampleType}_${org.organism}`;
    
    locations.forEach(location => {
      const value = zooplanktonGridData[organismKey]?.[location];
      if (value && value.trim()) {
        samples.push({
          sample_type: org.sampleType,
          organism: org.organism,
          location,
          value: value
        });
      }
    });
  });
  
  return samples;
};

/**
 * Convert grid data to display format for table
 * @param {Object} gridData - Grid data object
 * @param {string} organismKey - Organism key
 * @param {Array} locations - List of locations
 * @returns {Object} Formatted data for display
 */
export const getRowDisplayData = (gridData, organismKey, locations) => {
  const rowData = {};
  
  locations.forEach(location => {
    const value = gridData[organismKey]?.[location] || '';
    rowData[location] = formatNumber(value);
  });
  
  return rowData;
};

/**
 * Calculate sum for a specific location across organisms
 * @param {Array} organisms - List of organisms in group
 * @param {Object} gridData - Grid data
 * @param {string} location - Location to sum
 * @returns {number} Sum value
 */
export const calculateLocationSum = (organisms, gridData, location) => {
  return organisms.reduce((sum, organism) => {
    if (organism.isSum || organism.isTotal) return sum;
    
    const organismKey = `${organism.group}_${organism.species}`;
    const value = gridData[organismKey]?.[location] || '';
    const numValue = parseInt(value) || 0;
    
    return sum + numValue;
  }, 0);
};

/**
 * Validate clipboard data format
 * @param {Array<Array<string>>} data - Parsed clipboard data
 * @returns {Object} { valid: boolean, error?: string }
 */
export const validateClipboardData = (data) => {
  if (!data || data.length === 0) {
    return { valid: false, error: 'Ingen data funnet' };
  }
  
  // Check if all rows have same number of columns
  const columnCount = data[0].length;
  const invalidRows = data.filter(row => row.length !== columnCount);
  
  if (invalidRows.length > 0) {
    return { valid: false, error: 'Ulik antall kolonner i radene' };
  }
  
  return { valid: true };
};
